import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMDialog } from "../PMDialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";
import {
  buildActivityGroupSections,
  filterActivityGroupSections,
  GROUP_HEADER_CLASS,
  hasRealActivityGroups,
} from "./projectActivityGroupUtils";
import { useProjectActivityPickerData } from "./useProjectActivityPickerData";

function activityPrimary(row) {
  return row?.display_title ?? row?.displayTitle ?? row?.title ?? "";
}

function activitySecondary(row) {
  if (!row) return null;
  const groupTitle = row.group?.title || row.group_title || row.groupTitle || null;
  const num = row.number != null && row.number !== "" ? String(row.number) : null;
  const type = row.activity_type && row.activity_type !== "standard" ? row.activity_type : null;
  const bits = [groupTitle, num, type && type !== "standard" ? type : null].filter(Boolean);
  return bits.length ? bits.join(" · ") : null;
}

/**
 * Project activity line picker — same UX as {@link ./projectSelector ProjectSelector}
 * (compact trigger, PMDialog dense, edge-to-edge list on small screens).
 */
export const ProjectActivitySelector = (props) => {
  const {
    setFieldValue,
    errors,
    onChange,
    showField,
    project,
    listFetchParams = null,
    noLabel,
    clearAfterSelect = false,
    excludeIds = [],
    multiple = false,
    onMultipleSelect,
    triggerLabelKey,
  } = props;

  const name = props?.name ?? "project_activity_id";
  const label = props?.label;
  const noneLabelKey = props.noneLabelKey ?? "project_activity.none";

  const { t } = useTranslation();
  const nonePrimary = t(noneLabelKey);

  const fieldLabel =
    label === false ? null : label != null && label !== "" ? label : t("project_activity.label");
  const dialogTitle = fieldLabel ?? t("project_activity.dialog_title");

  const [open, setOpen] = useState(false);

  // Allow the dialog to be opened/closed from a parent (controlled `open` prop),
  // mirroring ProjectSelector. Lets callers render their own trigger.
  useEffect(() => {
    if (props.open === true) {
      if (props.disabled || !project?.id) return;
      setOpen(true);
    } else if (props.open === false) {
      setOpen(false);
    }
  }, [props.open, props.disabled, project?.id]);

  const [query, setQuery] = useState("");
  const searchInputRef = useAutofocusOnOpen(open);
  const {
    activities: rows,
    groups,
    loading,
    error: loadFailed,
  } = useProjectActivityPickerData(project?.id, {
    enabled: Boolean(project?.id),
    listFetchParams,
  });
  const loadError = loadFailed ? t("project_activity.load_error") : null;

  const [selected, setSelected] = useState({
    id: null,
    field1: nonePrimary,
    field2: null,
  });
  const [pendingIds, setPendingIds] = useState([]);

  const selectedFromProps = props?.selected;

  useEffect(() => {
    if (selectedFromProps?.id != null) {
      setSelected({
        id: selectedFromProps.id,
        field1: activityPrimary(selectedFromProps) || nonePrimary,
        field2: activitySecondary(selectedFromProps),
      });
    } else {
      setSelected({
        id: null,
        field1: nonePrimary,
        field2: null,
      });
    }
  }, [selectedFromProps, nonePrimary]);

  const handleOpen = () => {
    if (props.disabled || !project?.id) return;
    if (multiple) setPendingIds([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
    props.onClose?.();
  };

  const normalizeRow = useCallback((row) => {
    const id = row?.id;
    const primary = activityPrimary(row) || `#${id}`;
    return {
      id,
      value: id,
      primary,
      secondary: activitySecondary(row),
      object: row,
    };
  }, []);

  const handleSelect = (item) => {
    if (item?.isHeader) return;
    if (multiple) {
      if (item.id == null) return;
      setPendingIds((prev) => {
        const key = String(item.id);
        return prev.some((id) => String(id) === key)
          ? prev.filter((id) => String(id) !== key)
          : [...prev, item.id];
      });
      return;
    }

    if (item.id == null) {
      setSelected({ id: null, field1: nonePrimary, field2: null });
      if (setFieldValue) {
        setFieldValue(name, null);
        setFieldValue("project_activity", null);
      }
      if (onChange) {
        onChange({ target: { name, value: null, object: null } });
      }
    } else {
      const obj = item.object ?? item;
      setSelected({
        id: item.id,
        field1: item.primary ?? nonePrimary,
        field2: item.secondary ?? null,
      });
      const idStr = obj?.id != null ? String(obj.id) : String(item.id);
      if (setFieldValue) {
        setFieldValue(name, idStr);
        setFieldValue("project_activity", obj);
      }
      if (onChange) {
        onChange({
          target: { name, value: obj?.id ?? item.id, object: obj },
        });
      }
      if (clearAfterSelect) {
        setSelected({ id: null, field1: nonePrimary, field2: null });
      }
    }
    handleClose();
  };

  const items = useMemo(() => {
    const excluded = new Set((excludeIds || []).map((id) => String(id)));
    const base = [{ id: null, value: null, primary: nonePrimary, secondary: null, object: null }];
    const mapped = rows
      .filter((row) => !excluded.has(String(row.id)))
      .map((row) => normalizeRow(row));
    return [...base, ...mapped];
  }, [rows, nonePrimary, normalizeRow, excludeIds]);

  const filteredItems = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const excluded = new Set((excludeIds || []).map((id) => String(id)));
    const visibleRows = rows.filter((row) => !excluded.has(String(row.id)));
    const noneItem = {
      id: null,
      value: null,
      primary: nonePrimary,
      secondary: null,
      object: null,
    };
    const matchesItem = (it) => {
      if (it.id == null) return true;
      if (!q) return true;
      const p = (it.primary || "").toLowerCase();
      const s = (it.secondary || "").toLowerCase();
      return p.includes(q) || s.includes(q);
    };

    const grouped = hasRealActivityGroups(visibleRows, groups);
    const sections = buildActivityGroupSections(visibleRows, groups, {
      ungroupedLabel: t("project_activity.ungrouped"),
      hideEmptyGroups: Boolean(q),
    });
    const filteredSections = q
      ? filterActivityGroupSections(sections, (row) => {
          const it = normalizeRow(row);
          const groupTitle = (row?.group?.title || "").toLowerCase();
          return matchesItem(it) || (q && groupTitle.includes(q));
        })
      : sections;
    if (!grouped) {
      const list = (multiple ? items.filter((it) => it.id != null) : items).filter(matchesItem);
      return list;
    }
    const out = multiple ? [] : [noneItem];
    for (const section of filteredSections) {
      out.push({
        id: `header-${section.key}`,
        isHeader: true,
        primary: section.title || t("project_activity.ungrouped"),
        count: section.activities.length,
      });
      out.push(
        ...section.activities.map((row) => ({
          ...normalizeRow({
            ...row,
            group: row.group || (section.isUngrouped ? null : { id: section.id, title: section.title }),
          }),
          grouped: true,
        }))
      );
    }
    return out;
  }, [items, query, multiple, rows, groups, excludeIds, nonePrimary, normalizeRow, t]);

  const pendingItems = useMemo(
    () =>
      filteredItems.filter(
        (item) =>
          item.id != null &&
          !item.isHeader &&
          pendingIds.some((id) => String(id) === String(item.id))
      ),
    [filteredItems, pendingIds]
  );

  const handleConfirmMultiple = () => {
    const selectedObjects = pendingItems.map((item) => item.object ?? item).filter((row) => row?.id);
    if (onMultipleSelect) {
      onMultipleSelect(selectedObjects);
    } else if (onChange) {
      onChange({ target: { name, objects: selectedObjects } });
    }
    setPendingIds([]);
    handleClose();
  };

  const hasSelection = multiple ? false : selected?.id != null;
  const triggerText = multiple
    ? t(triggerLabelKey || "project.event.select_activities")
    : !project?.id
      ? t("project_activity.select_project_first")
      : selected.field1;
  const isDisabled = props.disabled || !project?.id;
  const showLabelRow = label !== false && noLabel !== false;

  const triggerButton =
    showField !== false ? (
      <div>
        {showLabelRow && fieldLabel && (
          <label className="block text-sm font-medium text-gray-700 mb-1">{fieldLabel}</label>
        )}
        <button
          type="button"
          disabled={isDisabled}
          onClick={handleOpen}
          className={[
            "relative w-full flex items-center justify-between",
            "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
            "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
            isDisabled
              ? "cursor-not-allowed border-gray-200 text-gray-400"
              : !project?.id
              ? "border-gray-200 text-gray-500"
              : hasSelection
              ? "border-blue-400 text-gray-900 hover:border-blue-500"
              : "border-gray-300 text-gray-500 hover:border-gray-400",
          ].join(" ")}
        >
          <div className="min-w-0 text-left">
            {!project?.id ? (
              <span className="truncate text-gray-500">{t("project_activity.select_project_first")}</span>
            ) : (
              <>
                <span className="truncate">{triggerText}</span>
                {!multiple && selected.field2 && (
                  <span className="ml-2 text-xs text-gray-400 truncate">{selected.field2}</span>
                )}
              </>
            )}
          </div>
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
        </button>
        {errors?.[name] && <p className="mt-1 text-xs text-red-600">{errors[name]}</p>}
      </div>
    ) : null;

  const dialogContent = (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">{dialogTitle}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("project_activity.search_placeholder")}
            autoFocus
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {loading ? (
          <SelectorListSkeleton />
        ) : loadError ? (
          <div className="p-6 text-center text-sm text-red-600">{loadError}</div>
        ) : !project?.id ? (
          <div className="p-6 text-center text-sm text-gray-500">
            {t("project_activity.select_project_first")}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">{t("project_activity.empty")}</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filteredItems.map((item) => {
              if (item.isHeader) {
                return (
                  <li
                    key={item.id}
                    role="presentation"
                    className={GROUP_HEADER_CLASS}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "10px 16px",
                      fontSize: 12,
                      fontWeight: 800,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "#0B3A78",
                      background: "#EAF3FD",
                      borderTop: "1px solid #BFDCF7",
                      borderBottom: "1px solid #BFDCF7",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <span className="truncate">{item.primary}</span>
                    {item.count != null ? (
                      <span style={{ color: "#1463C7", fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
                        {item.count}
                      </span>
                    ) : null}
                  </li>
                );
              }
              const isSelected = multiple
                ? pendingIds.some((id) => String(id) === String(item.id))
                : item.id == null
                  ? selected?.id == null
                  : String(item.id) === String(selected?.id);
              return (
                <li
                  key={item.id ?? "none"}
                  onClick={() => handleSelect(item)}
                  className={[
                    "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                    item.grouped ? "pl-7" : "",
                    isSelected ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50 text-gray-900",
                  ].join(" ")}
                >
                  <div className="min-w-0 flex items-center gap-3">
                    {multiple ? (
                      <input
                        type="checkbox"
                        readOnly
                        checked={isSelected}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                    ) : null}
                    <div>
                      <p className="text-sm font-medium truncate">{item.primary}</p>
                      {item.secondary && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{item.secondary}</p>
                      )}
                    </div>
                  </div>
                  {!multiple && isSelected && (
                    <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {multiple ? (
        <div className="flex-shrink-0 border-t border-gray-200 p-3 sm:p-4 flex items-center justify-between gap-3">
          <span className="text-sm text-gray-600">
            {t("project.plan.multi_select_selected", { count: pendingIds.length })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              disabled={pendingIds.length === 0}
              onClick={handleConfirmMultiple}
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {t("project.plan.add_selected", { count: pendingIds.length })}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {triggerButton}
      <PMDialog
        open={open}
        onClose={handleClose}
        size={multiple ? "lg" : "md"}
        dense
        closeOnOutsideClick
        zIndex={props.zIndex ?? 2600}
      >
        {dialogContent}
      </PMDialog>
    </>
  );
};
